import { useEffect, useMemo, useState } from "react";
import { cn } from "@/utils/format";

/** Extensible channel model for future server/DB/API/AI status. */
export type StatusChannelId =
  | "internet"
  | "server"
  | "database"
  | "api"
  | "ai"
  | (string & {});

export type StatusTone = "online" | "offline" | "degraded" | "unknown";

export interface StatusChannel {
  id: StatusChannelId;
  label: string;
  tone: StatusTone;
  detail: string;
}

function readInternetChannel(): StatusChannel {
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;
  return {
    id: "internet",
    label: online ? "Online" : "Offline",
    tone: online ? "online" : "offline",
    detail: online ? "Connected to the Internet" : "No Internet Connection",
  };
}

/**
 * Connectivity / status pill.
 * Currently surfaces browser internet connectivity only.
 * Pass additional `channels` later for server, database, API, AI, etc.
 */
export function ConnectivityStatus({
  channels: extraChannels = [],
  className,
}: {
  channels?: StatusChannel[];
  className?: string;
}) {
  const [internet, setInternet] = useState<StatusChannel>(() => readInternetChannel());

  useEffect(() => {
    const sync = () => setInternet(readInternetChannel());
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    sync();
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, []);

  const channels = useMemo(
    () => [internet, ...extraChannels.filter((c) => c.id !== "internet")],
    [internet, extraChannels]
  );

  // Primary indicator = internet connectivity (product requirement).
  // Aggregate helpers stay available for multi-status expansion.
  const primary = channels[0] ?? internet;
  const allOnline = channels.every((c) => c.tone === "online");

  return (
    <div
      className={cn("connectivity-status", className)}
      title={primary.detail}
      role="status"
      aria-live="polite"
      aria-label={primary.detail}
      data-tone={primary.tone}
      data-all-online={allOnline ? "true" : "false"}
    >
      <span className="connectivity-status__dot" aria-hidden />
      <span className="connectivity-status__label">{primary.label}</span>
      {/* Future: map `channels` into a richer multi-status popover */}
    </div>
  );
}
